import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"

import type { CombinePalletsAdjustmentFormValues } from "../../schemas"

import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { DOCUMENT_UPLOAD_MIME_TYPES, DOCUMENT_UPLOAD_TYPES_LABEL } from "@/components/upload/constants"
import FileInput from "@/components/upload/FileInput"
import type { Option } from "@/types/form-fields"

interface CombinePalletsAdjustmentFormDetailsProps {
  referenceNumbers: Set<number>
}

export const CombinePalletsAdjustmentFormDetails: FC<CombinePalletsAdjustmentFormDetailsProps> = ({
  referenceNumbers,
}) => {
  const { control } = useFormContext<CombinePalletsAdjustmentFormValues>()

  const targetReferenceOptions = useMemo<Option<number>[]>(
    () =>
      Array.from(referenceNumbers, (referenceNumber) => ({
        value: referenceNumber,
        label: String(referenceNumber),
      })),
    [referenceNumbers]
  )

  return (
    <Box className='flex flex-col overflow-hidden rounded-lg border border-gray-400'>
      <Typography variant='h6' className='p-4 font-bold leading-10'>
        3. Adjustment Details
      </Typography>
      <Box className='flex min-h-0 flex-1 flex-col gap-y-6 overflow-y-auto px-4 pb-4'>
        <FormSelect
          control={control}
          name='targetReferenceNumber'
          label='Target Reference ID'
          required
          placeholder='Select reference ID...'
          options={targetReferenceOptions}
          disabled={targetReferenceOptions.length < 2}
          data-testid='inventory.combine-pallets-adjustment-form-details.target-reference-field'
        />
        <FormTextField
          control={control}
          name='comment'
          label='Comment'
          placeholder='Enter comment'
          multiline
          rows={4}
          data-testid='inventory.combine-pallets-adjustment-form-details.comment-field'
        />
        <FileInput<CombinePalletsAdjustmentFormValues>
          name='document'
          allowedMimeTypes={DOCUMENT_UPLOAD_MIME_TYPES}
          allowedTypesLabel={DOCUMENT_UPLOAD_TYPES_LABEL}
          data-testid='inventory.combine-pallets-adjustment-form-details.document-field'
        />
      </Box>
    </Box>
  )
}
